import { Helmet } from "react-helmet";
import { useState } from "react";
import {
  FaPlus,
  FaTrashCan,
  FaSpinner,
  FaUser,
  FaPhone,
  FaWifi,
  FaServer,
  FaLocationDot,
  FaTag,
  FaBriefcase,
  FaCircleUser,
  FaTriangleExclamation,
} from "react-icons/fa6";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../components/Header";
import { BtnDefault } from "../../../../components/buttons/BtnDefault";
import { useNavigate } from "react-router-dom";
import { useHookPppoePsb } from "../../../../hooks/useHookPppoePsb";
import { PppoePsbService } from "../../../../services/PppoePsbService";

/* ============================================
   Skeleton Loading
   ============================================ */
const SkeletonRow = ({ cols = 11 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-5 py-4">
        <div
          className="h-3.5 bg-slate-700/50 rounded-md"
          style={{ width: `${35 + Math.random() * 55}%` }}
        />
      </td>
    ))}
  </tr>
);

const SkeletonCard = () => (
  <div
    className="rounded-2xl p-5 animate-pulse"
    style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
  >
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-slate-700/50" />
        <div className="space-y-2">
          <div className="h-4 bg-slate-700/50 rounded-md w-28" />
          <div className="h-3 bg-slate-700/30 rounded-md w-20" />
        </div>
      </div>
      <div className="h-6 bg-slate-700/30 rounded-full w-16" />
    </div>
    <div className="grid grid-cols-2 gap-3 pt-3" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}>
      {[...Array(6)].map((_, i) => (
        <div key={i} className="h-9 bg-slate-700/25 rounded-lg" />
      ))}
    </div>
  </div>
);

/* ============================================
   Status Badge
   ============================================ */
const StatusBadge = ({ status }) => {
  const isActive = status === "aktif";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${
        isActive
          ? "bg-emerald-500/12 text-emerald-400 border border-emerald-500/20"
          : "bg-red-500/12 text-red-400 border border-red-500/20"
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-400" : "bg-red-400"}`} />
      {status}
    </span>
  );
};

/* ============================================
   Delete Button
   ============================================ */
const DeleteBtn = ({ onClick, isLoading, size = "md" }) => {
  const sizes = {
    sm: "px-2.5 py-1.5 text-xs gap-1.5",
    md: "px-3 py-2 text-sm gap-2",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isLoading}
      className={`inline-flex items-center font-semibold rounded-xl transition-all duration-200 ${
        sizes[size]
      } ${
        isLoading
          ? "text-red-400/60 cursor-not-allowed"
          : "text-red-400 hover:text-red-300 hover:bg-red-500/10 active:scale-95 cursor-pointer"
      }`}
      aria-label="Hapus data"
    >
      {isLoading ? <FaSpinner className="animate-spin" /> : <FaTrashCan />}
      {size === "md" && <span>Hapus</span>}
    </button>
  );
};

/* ============================================
   Table Columns Config
   ============================================ */
const columns = [
  { key: "no", label: "No", align: "center", width: "55px" },
  { key: "status", label: "Status", align: "center", width: "110px" },
  { key: "id_pel", label: "ID Pel", align: "left" },
  { key: "name", label: "Nama", align: "left" },
  { key: "phone", label: "No. Telepon", align: "left" },
  { key: "profile", label: "Profile", align: "left" },
  { key: "nas", label: "NAS", align: "left" },
  { key: "pop", label: "POP", align: "left" },
  { key: "odp", label: "ODP", align: "left" },
  { key: "reseller", label: "Reseller", align: "left" },
  { key: "action", label: "Aksi", align: "center", width: "90px" },
];

/* ============================================
   KOMPONEN: PppoePsb
   ============================================ */
export const PppoePsb = () => {
  const [showSidebar, setShowSidebar] = useState(false);
  const navigate = useNavigate();
  const [deleteId, setDeleteId] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { data, loading, refreshData, keyword, setKeyword } = useHookPppoePsb();

  /* ---------- Delete Handler ---------- */
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setLoadingDelete(true);
    try {
      await PppoePsbService.delete({ id: deleteId });
      refreshData();
      setShowConfirm(false);
      setDeleteId(null);
    } catch (error) {
      console.error("Delete error:", error);
    } finally {
      setLoadingDelete(false);
    }
  };

  const cancelDelete = () => {
    setShowConfirm(false);
    setDeleteId(null);
  };

  /* ---------- Render ---------- */
  return (
    <>
      <Helmet>
        <title>PPPoE PSB - Radius Trend</title>
        <meta
          name="description"
          content="Data PSB (Pemasangan Sambungan Baru) PPPoE — kelola status pemasangan, pelanggan, dan infrastruktur jaringan."
        />
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <h2 className="text-base md:text-xl font-display font-bold text-gray-100">
            Data PSB
          </h2>
          <BtnDefault onClick={() => navigate("/dashboard/pppoe/psb/create")}>
            <FaPlus className="text-xs hidden sm:block" />
            <span>Tambah PSB</span>
          </BtnDefault>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-5">
          {/* ===== Search ===== */}
          <section
            className="rounded-2xl p-4"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Pencarian"
          >
            <div className="w-full max-w-sm">
              <div className="relative">
                <i
                  className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  placeholder="Cari nama, ID pelanggan..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                />
              </div>
            </div>
          </section>

          {/* ===== Desktop Table ===== */}
          <section
            className="hidden lg:block rounded-2xl overflow-hidden"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Tabel PSB PPPoE"
          >
            <div className="overflow-x-auto">
              <table className="min-w-max max-w-2xl text-sm">
                {/* Head */}
                <thead>
                  <tr
                    className="border-b"
                    style={{ background: "rgba(15, 23, 42, 0.7)", borderColor: "rgba(51, 65, 85, 0.3)" }}
                  >
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${
                          col.align === "center" ? "text-center" : "text-left"
                        }`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Body */}
                <tbody className="divide-y" style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}>
                  {loading && !data && (
                    <>{[...Array(6)].map((_, i) => <SkeletonRow key={i} cols={columns.length} />)}</>
                  )}

                  {!loading && data?.length === 0 && (
                    <tr >
                      <td colSpan={columns.length} className="px-5 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center"
                            style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                          >
                            <FaCircleUser className="text-2xl text-slate-600" />
                          </div>
                          <div>
                            <p className="text-slate-400 font-medium">Tidak ada data PSB ditemukan</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {keyword ? "Coba ubah kata kunci pencarian" : "Belum ada data PSB yang terdaftar"}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    data?.map((item, index) => (
                      <tr
                        key={item.id}
                        className="transition-colors duration-150 cursor-pointer"
                        style={{ background: "transparent" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0, 180, 216, 0.04)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                        onClick={() => navigate("/dashboard/pppoe/psb/detail/1")}
                      >
                        <td className="px-5 py-3.5 text-center text-slate-500 font-mono text-xs">
                          {index + 1}
                        </td>

                        <td className="px-5 py-3.5 text-center">
                          <StatusBadge status={item.status} />
                        </td>

                        <td className="px-5 py-3.5">
                          <span className="font-mono text-xs text-cyan-400/80 bg-cyan-500/8 px-2 py-0.5 rounded border border-cyan-500/10">
                            {item.id_pel}
                          </span>
                        </td>

                        <td className="px-5 py-3.5">
                          <span className="font-semibold text-gray-100">{item.name}</span>
                        </td>

                        <td className="px-5 py-3.5 text-slate-400 text-xs">{item.phone_number || "-"}</td>

                        <td className="px-5 py-3.5 text-slate-300 text-xs">{item?.profile?.name || "-"}</td>

                        <td className="px-5 py-3.5 text-slate-300 text-xs">{item?.nas?.name || "-"}</td>

                        <td className="px-5 py-3.5 text-slate-300 text-xs">{item?.pop?.name || "-"}</td>

                        <td className="px-5 py-3.5 text-slate-300 text-xs">{item?.odp?.name || "-"}</td>

                        <td className="px-5 py-3.5 text-slate-400 italic text-xs">{item.reseller?.name || "—"}</td>

                        <td className="px-5 py-3.5 text-center">
                          <DeleteBtn onClick={() => handleDeleteClick(item.id)} size="sm" />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ===== Mobile Cards ===== */}
          <section className="lg:hidden space-y-3" aria-label="Daftar PSB PPPoE">
            {loading && !data && <>{[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}</>}

            {!loading && data?.length === 0 && (
              <div
                className="rounded-2xl p-12 text-center"
                style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                >
                  <FaCircleUser className="text-2xl text-slate-600" />
                </div>
                <p className="text-slate-400 font-medium">Tidak ada data PSB ditemukan</p>
                <p className="text-xs text-slate-500 mt-1">{keyword ? "Coba ubah pencarian" : "Belum ada data"}</p>
              </div>
            )}

            {!loading &&
              data?.map((item, index) => (
                <article
                  key={item.id}
                  className="rounded-2xl overflow-hidden transition-all duration-200"
                  style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "rgba(0, 180, 216, 0.15)";
                    e.currentTarget.style.background = "rgba(15, 23, 42, 0.7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(51, 65, 85, 0.3)";
                    e.currentTarget.style.background = "rgba(15, 23, 42, 0.5)";
                  }}
                >
                  {/* Header */}
                  <div className="p-4 flex justify-between items-center" style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.15)" }}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 font-display font-bold text-xs text-ocean-400"
                        style={{ background: "rgba(0, 180, 216, 0.1)", border: "1px solid rgba(0, 180, 216, 0.15)" }}
                      >
                        {index + 1}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-100 leading-tight text-sm truncate">{item.name}</h3>
                        <p className="text-[11px] text-slate-500 font-mono mt-0.5 truncate">{item.id_pel}</p>
                      </div>
                    </div>
                    <StatusBadge status={item.status} />
                  </div>

                  {/* Body */}
                  <div className="p-4 grid grid-cols-2 gap-y-3 gap-x-2" style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.15)" }}>
                    {[
                      { icon: FaPhone, label: "Telepon", value: item.phone_number, color: "text-ocean-400" },
                      { icon: FaWifi, label: "Profile", value: item?.profile?.name, color: "text-violet-400" },
                      { icon: FaServer, label: "NAS", value: item?.nas?.name, color: "text-cyan-400" },
                      { icon: FaLocationDot, label: "POP", value: item?.pop?.name, color: "text-amber-400" },
                      { icon: FaTag, label: "ODP", value: item?.odp?.name, color: "text-emerald-400" },
                      { icon: FaBriefcase, label: "Reseller", value: item.reseller?.name, color: "text-slate-400" },
                    ].map((row, i) => {
                      const Icon = row.icon;
                      return (
                        <div key={i} className="flex items-center gap-2 min-w-0">
                          <Icon className={`${row.color} text-xs flex-shrink-0`} />
                          <div className="min-w-0">
                            <p className="text-[10px] text-slate-600 uppercase font-semibold tracking-wider leading-none">{row.label}</p>
                            <p className="text-sm text-slate-300 truncate mt-0.5">{row.value || "—"}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Footer */}
                  <div className="px-4 py-3 flex justify-end" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}>
                    <DeleteBtn onClick={() => handleDeleteClick(item.id)} size="md" />
                  </div>
                </article>
              ))}
          </section>
        </main>
      </DashboardLayout>

      {/* ===== Confirm Delete Modal ===== */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ animation: "fadeIn 0.2s ease-out" }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={cancelDelete}
          />

          {/* Dialog */}
          <div
            className="relative w-full max-w-sm rounded-2xl p-6"
            style={{
              background: "rgba(11, 20, 38, 0.97)",
              border: "1px solid rgba(51, 65, 85, 0.4)",
              boxShadow: "0 25px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(248, 113, 113, 0.08)",
              backdropFilter: "blur(20px)",
              animation: "scaleIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            {/* Warning icon */}
            <div className="flex justify-center mb-5">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{
                  background: "rgba(248, 113, 113, 0.1)",
                  border: "2px solid rgba(248, 113, 113, 0.2)",
                }}
              >
                <FaTriangleExclamation className="text-2xl text-red-400" />
              </div>
            </div>

            <h3 className="font-display text-lg font-bold text-gray-100 text-center mb-2">
              Konfirmasi Hapus
            </h3>
            <p className="text-sm text-slate-400 text-center leading-relaxed mb-8">
              Data PSB yang dihapus tidak dapat dikembalikan. Apakah Anda yakin ingin melanjutkan?
            </p>

            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                disabled={loadingDelete}
                className="flex-1 py-3 px-4 rounded-xl text-sm font-semibold text-slate-300 border transition-all duration-200 hover:bg-slate-700/40 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                style={{ borderColor: "rgba(51, 65, 85, 0.3)" }}
              >
                Batal
              </button>
              <button
                onClick={confirmDelete}
                disabled={loadingDelete}
                className="flex-1 py-3 px-4 rounded-xl text-sm font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                style={{
                  background: "linear-gradient(135deg, #dc2626, #ef4444)",
                  boxShadow: "0 4px 15px rgba(220, 38, 38, 0.3)",
                }}
              >
                {loadingDelete ? (
                  <>
                    <FaSpinner className="animate-spin text-sm" />
                    Menghapus...
                  </>
                ) : (
                  <>
                    <FaTrashCan className="text-xs" />
                    Hapus
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.92) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </>
  );
};